'use client';

import { toast } from '@/hooks/use-toast';

/**
 * Patterns indicating a stale client bundle after deployment.
 *
 * 'Loading chunk' was REMOVED (2026-05-04) — too broad. It matches ANY webpack
 * chunk load failure including transient network blips, not just post-deploy
 * stale bundles. Use the explicit `ChunkLoadError` name instead.
 */
const STALE_BUNDLE_PATTERNS = [
  'was not found on the server',
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
] as const;

let notified = false;

export function isStaleBundleError(message: string): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return STALE_BUNDLE_PATTERNS.some((p) => lower.includes(p.toLowerCase()));
}

/**
 * Show a user-controlled refresh toast for stale bundle errors.
 *
 * Does NOT auto-reload. Auto-reload during a Next.js navigation transition
 * reloads the SOURCE url because pushState commits only at RSC commit time —
 * if the fetch fails before commit, reload returns the user to the previous
 * page, producing the "phantom-nav" snap-back symptom.
 *
 * Replaces three previous redundant auto-reload paths in ServerActionRecovery,
 * get-query-client, and reporter.ts.
 *
 * @returns true if a stale bundle error was detected and user was notified
 */
export function notifyStaleBundle(error: unknown): boolean {
  if (typeof window === 'undefined') return false;

  const message = error instanceof Error ? error.message : String(error ?? '');
  if (!isStaleBundleError(message)) return false;

  if (notified) return true;
  notified = true;

  toast.error('A new version of the app is available', {
    duration: Infinity,
    description: 'Please refresh to load the latest. Your current work is not affected.',
    action: {
      label: 'Refresh',
      onClick: () => window.location.reload(),
    },
  });

  return true;
}
